import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type {
  TypeExchangeDetailData,
  TypeGetExchangeOrdersExchangeReceiptIdParams,
  TypeGetExchangeOrdersExchangeReceiptIdResponse,
} from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * 상품 코드가 같은 단위끼리 묶어서 포맷팅하는 함수
 * - 상품코드 기준으로 그룹핑
 * - 검수 단위 별 정보는 inspectionDetails 배열에 추가
 */
const formatExchangeDetailData = (
  data: TypeGetExchangeOrdersExchangeReceiptIdResponse['data']
): TypeExchangeDetailData => {
  const {
    returnOrder: { returnGoodsInspections },
  } = data;

  const groupedByGoodsCode: Record<string, TypeExchangeDetailData['returnOrder']['returnGoodsInspections'][number]> =
    {};

  returnGoodsInspections.forEach((inspection) => {
    const {
      goodsCode,
      goodsName,
      returnQuantity,
      returnReason,
      inspectionQuantity,
      inboundStatus,
      inspectionStatus,
      inspectionImageUrls,
      inspectionAt,
      unlistedYn,
    } = inspection;

    if (!groupedByGoodsCode[goodsCode]) {
      groupedByGoodsCode[goodsCode] = {
        goodsCode,
        goodsName,
        returnQuantity,
        returnReason,
        inspectionDetails: [],
      };
    }

    // 검수 단위 별 정보 추가
    groupedByGoodsCode[goodsCode].inspectionDetails.push({
      inspectionQuantity,
      inboundStatus,
      inspectionStatus,
      inspectionImageUrls,
      inspectionAt,
      unlistedYn,
    });
  });

  const formattedReturnGoodsInspections = Object.values(groupedByGoodsCode);

  return {
    ...data,
    returnOrder: {
      ...data.returnOrder,
      returnGoodsInspections: formattedReturnGoodsInspections,
    },
  };
};

/**
 * [교환 신청/조회] 교환 신청 상세 조회 api 요청 훅
 */
const useGetExchangeOrdersExchangeReceiptId = () => {
  const { runAsync } = useAsyncHandler();

  const getExchangeOrdersExchangeReceiptId = useAPI<
    TypeGetExchangeOrdersExchangeReceiptIdParams,
    TypeGetExchangeOrdersExchangeReceiptIdResponse
  >({
    endpoint: API_ENDPOINT.CLAIM.GET_EXCHANGE_ORDERS_EXCHANGE_RECEIPT_ID,
    method: 'get',
  });

  const requestExchangeOrdersDetail = async (
    { exchangeReceiptId }: TypeGetExchangeOrdersExchangeReceiptIdParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeExchangeDetailData) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getExchangeOrdersExchangeReceiptId({ pathParams: `/${exchangeReceiptId}` }), {
      onSuccess: ({ data }) => onSuccess?.(formatExchangeDetailData(data)),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestExchangeOrdersDetail };
};

export default useGetExchangeOrdersExchangeReceiptId;
